import React from 'react'
import PropTypes from 'prop-types'
import { MyButton, MyFeaturedIcon } from '@interstellar-component'

import { formatRp, formatLongDate, packageLabel } from '../../wallet.format'
import { subscriptionStatusBadge } from '../../billing.format'

/**
 * Left card — which package the company is on and what an enquiry costs.
 *
 * Two sources, deliberately: the NAME and TARIF come from the pricing registry
 * (`pricing.resolve`, which is golongan-resolved and live), while the STATUS and
 * period end come from the subscription row. They can legitimately disagree —
 * a company mid-review keeps a Korporat tarif under an active subscription — so
 * neither is derived from the other.
 */
function PackageCard({ pricing, subscription, onChangePackage, onCancelPending, canAct }) {
  const pkg = pricing?.package || null
  const name = packageLabel(pkg) || (pricing?.legacy_subscription ? 'Active plan' : 'Not subscribed yet')
  const tarif = pricing?.tarif_enquiry_idr
  // `covered_until` = period_end of the newest PAID invoice — what the company
  // actually bought. NOT `current_period_end`, which is the billing pointer and
  // already sits a full cycle ahead the moment the first invoice is paid.
  const expiresAt = formatLongDate(subscription?.covered_until)
  const badge = subscription ? subscriptionStatusBadge(subscription.status) : null

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-xl border border-gray/200 bg-white shadow-xs lg:w-[417px] lg:shrink-0">
      <div className="flex flex-col gap-5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-4">
            <MyFeaturedIcon icon="Package" color="Brand" size="lg" />
            <h3 className="text-2xl font-semibold text-gray/900">{name}</h3>
          </div>
          {badge ? (
            <span
              className={`mt-1 inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs-medium ${badge.cls}`}
            >
              {badge.label}
            </span>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm-regular text-gray/600">Fee per enquiry</span>
          {/* null tarif is a real state, not a zero: the company cannot be
              priced from the registry (no package, or a pre-cutover
              subscription). Showing Rp0 would read as "free". */}
          <span className="text-md-semibold text-gray/900">
            {tarif != null ? formatRp(tarif) : '—'}
          </span>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray/200 bg-gray/50 px-4 py-4">
        {expiresAt ? (
          <span className="inline-flex items-center rounded-full border border-gray/200 bg-white px-2.5 py-0.5 text-xs-medium text-gray/700">
            Expires {expiresAt}
          </span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {/* Only present for a never-paid subscribe — the single-company view's
              lone exit from a stranded pending_payment (cancelling the invoice
              alone never clears the subscription row). */}
          {onCancelPending ? (
            <MyButton
              color="error"
              variant="text"
              size="sm"
              type="button"
              disabled={!canAct}
              onClick={onCancelPending}
            >
              <span className="text-sm-semibold">Cancel</span>
            </MyButton>
          ) : null}
          <MyButton
            color="secondary"
            variant="outlined"
            size="sm"
            type="button"
            disabled={!canAct}
            onClick={onChangePackage}
          >
            <span className="text-sm-semibold">
              {subscription ? 'Change plan' : 'Choose plan'}
            </span>
          </MyButton>
        </div>
      </div>
    </div>
  )
}

PackageCard.propTypes = {
  pricing: PropTypes.shape({
    package: PropTypes.object,
    tarif_enquiry_idr: PropTypes.number,
    legacy_subscription: PropTypes.bool,
  }),
  subscription: PropTypes.shape({
    status: PropTypes.string,
    covered_until: PropTypes.string,
  }),
  onChangePackage: PropTypes.func,
  onCancelPending: PropTypes.func,
  canAct: PropTypes.bool,
}

PackageCard.defaultProps = {
  pricing: null,
  subscription: null,
  onChangePackage: null,
  onCancelPending: null,
  canAct: false,
}

export default PackageCard
